import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Clock, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useCartStore, useAuthStore } from '@/store';
import { OrderSummaryCard, TipAndInstructions, OnlinePaymentStatus, ReadyTextOptIn, placeOnlineOrder, forgetOrder } from '@/components/checkout';
import { API } from '@/config/api';
import { orderPath } from '@/apps/order/lib/orderPath';
const PICKUP_LABELS = { asap: 'ASAP (~15 min)', '15': 'In 15 minutes', '30': 'In 30 minutes', '45': 'In 45 minutes', '60': 'In 1 hour' };
const NOT_ACCEPTING = {
  closed: 'The restaurant is closed right now.',
  tax_rate_not_set: 'This restaurant has not finished its online checkout setup.',
  online_payments_not_set_up: 'This restaurant is not taking online card payments yet.',
  no_online_store: 'This restaurant does not have an online store yet.',
};

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

// The cart's lines in the shape the online-order API prices from the menu
const orderLines = (items) => items.map(item => ({
  menu_item_id: item.menu_item_id,
  quantity: item.quantity,
  modifier_option_ids: item.selected_modifiers || [],
  notes: item.special_instructions ? item.special_instructions.slice(0, 300) : null,
}));

/**
 * chain29.com customer checkout. It uses the same online-order doors as
 * online.chain29.com: the server prices the cart, the customer pays on the
 * restaurant's secure Aster Gateway page (same tab; the gateway returns the
 * customer to chain29.com/order/status/<id>?t=), and the order is
 * paid - and reaches the kitchen - only on the gateway's verdict, which the
 * payment screen polls. A reload resumes from ?order=&t=.
 * Card terminals belong to the POS / kiosk, never to the customer website.
 */
export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resumeOrder = searchParams.get('order');
  const resumeToken = searchParams.get('t');
  const resuming = !!(resumeOrder && resumeToken);
  const { items, restaurantId, restaurantName, subtotal, fetchCart, clearCart, isLoading: cartLoading } = useCartStore();
  const { token, user } = useAuthStore();
  const hasFetched = useRef(false);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [config, setConfig] = useState(null);
  const [customer, setCustomer] = useState({
    name: user?.full_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
  });
  const [errors, setErrors] = useState({});
  const [smsNotify, setSmsNotify] = useState(false);
  const [pickupTime, setPickupTime] = useState('asap');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [tip, setTip] = useState(0);
  const [tipPercentage, setTipPercentage] = useState(18);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // the server's pricing: { code, discount_cents, subtotal_cents, tax_cents, total_cents }
  const [promoChecking, setPromoChecking] = useState(false);
  const [placing, setPlacing] = useState(false);

  // Cart (server-side, signed-in customer)
  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCart().finally(() => setInitialLoadComplete(true));
  }, [fetchCart, token, navigate]);

  useEffect(() => {
    if (initialLoadComplete && !cartLoading && items.length === 0 && !resuming) {
      toast.info('Your cart is empty');
      navigate('/');
    }
  }, [initialLoadComplete, cartLoading, items, navigate, resuming]);

  // Can this restaurant take an online order now, and what is its store's slug
  useEffect(() => {
    if (!restaurantId) return;
    axios.get(`${API}/online/restaurants/${restaurantId}/checkout-config`)
      .then(res => {
        setConfig(res.data);
        if (res.data.tip_presets?.length) setTipPercentage(res.data.tip_presets[Math.min(1, res.data.tip_presets.length - 1)]);
      })
      .catch(() => setConfig({ accepting_orders: false, reasons: ['unavailable'] }));
  }, [restaurantId]);

  const tipsEnabled = config?.tips_enabled !== false;
  useEffect(() => {
    if (tipPercentage > 0) setTip(Math.round(subtotal * tipPercentage) / 100);
  }, [subtotal, tipPercentage]);

  // A validated promo belongs to the cart it was checked against
  const cartKey = items.map(i => `${i.id}:${i.quantity}`).join(',');
  useEffect(() => { setAppliedPromo(null); }, [cartKey]);

  // Amounts: the cart at menu prices; tax and the total are the SERVER's - from the
  // promo check when a code is applied (it prices the cart), else on the payment screen.
  const subtotalCents = appliedPromo?.subtotal_cents ?? Math.round(subtotal * 100);
  const discountCents = appliedPromo?.discount_cents || 0;
  const rate = config?.tax_rate_percent;
  const taxCents = appliedPromo?.tax_cents ?? null;
  const tipCents = tipsEnabled ? Math.round(tip * 100) : 0;
  const totalCents = appliedPromo?.total_cents != null ? appliedPromo.total_cents + tipCents : null;
  const slug = config?.restaurant?.slug;

  const handleApplyPromo = async () => {
    if (!promoCode || !slug) return;
    setPromoChecking(true);
    try {
      const res = await axios.post(`${API}/online/${slug}/promo/validate`, { code: promoCode, items: orderLines(items) });
      if (res.data.valid) {
        setAppliedPromo({ code: res.data.code, discount_cents: res.data.discount_cents, subtotal_cents: res.data.subtotal_cents,
                          tax_cents: res.data.tax_cents, total_cents: res.data.total_cents });
        toast.success(`Promo applied! -$${(res.data.discount_cents / 100).toFixed(2)}`);
      } else {
        setAppliedPromo(null);
        toast.error(res.data.message || 'Invalid promo code');
      }
    } catch (error) {
      setAppliedPromo(null);
      toast.error(errorText(error, 'Could not check the promo code'));
    } finally {
      setPromoChecking(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!customer.name.trim()) next.name = 'Name is required';
    if (!customer.phone.trim()) next.phone = 'Phone number is required';
    else if (!/^\+?1?[-. ]?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(customer.phone.trim())) next.phone = 'Please enter a valid phone number';
    if (customer.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) next.email = 'Please enter a valid email';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePaid = async (_payment, orderId = resumeOrder, accessToken = resumeToken) => {
    forgetOrder();
    try { await clearCart(); } catch { /* the order is paid either way */ }
    toast.success('Payment received — your order is in!');
    navigate(orderPath(`confirmation/${orderId}?t=${accessToken}`), { replace: true });
  };

  const handlePlaceOrder = async () => {
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setPlacing(true);
    try {
      const answer = await placeOnlineOrder(slug, {
        items: orderLines(items),
        customer_name: customer.name.trim(),
        customer_phone: customer.phone.trim(),
        customer_email: customer.email.trim() || null,
        pickup_time: pickupTime,
        tip_cents: tipCents,
        notes: specialInstructions.trim() || null,
        promo_code: appliedPromo?.code || null,
        sms_notify: smsNotify,
      }, { customerToken: token });
      if (answer.payment_status === 'paid') {
        handlePaid(null, answer.order_id, answer.access_token);
        return;
      }
      // the order is in the URL: a reload resumes the payment screen
      setSearchParams({ order: String(answer.order_id), t: answer.access_token }, { replace: true });
    } catch (error) {
      toast.error(errorText(error, 'Could not place your order. Please try again.'));
    } finally {
      setPlacing(false);
    }
  };

  if (resuming) {
    return (
      <OnlinePaymentStatus orderId={resumeOrder} accessToken={resumeToken} restaurantName={restaurantName}
        cancelledReturn={searchParams.get('cancelled') === '1'} onPaid={handlePaid} />
    );
  }

  if (!initialLoadComplete || cartLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center" data-testid="checkout-loading">
        <div className="flex flex-col items-center gap-4">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
          <p className="text-muted-foreground">Loading your cart...</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const notAccepting = config && !config.accepting_orders;
  const notAcceptingText = notAccepting
    ? (config.reasons || []).map(r => NOT_ACCEPTING[r]).find(Boolean) || 'Online ordering is not available right now.'
    : null;

  const field = (key, label, props = {}) => (
    <div>
      <Label htmlFor={`checkout-${key}`} className="text-sm font-medium">{label}</Label>
      <Input
        id={`checkout-${key}`}
        value={customer[key]}
        onChange={(e) => { setCustomer({ ...customer, [key]: e.target.value }); setErrors({ ...errors, [key]: '' }); }}
        className={errors[key] ? 'border-red-500' : ''}
        data-testid={`checkout-${key}`}
        {...props}
      />
      {errors[key] && <p className="text-sm text-red-600 mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-button">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Checkout</h1>
            <p className="text-muted-foreground">From {restaurantName}</p>
          </div>
        </div>

        {notAccepting && (
          <div className="mb-6 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm" data-testid="not-accepting">
            {notAcceptingText}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Store className="h-5 w-5 text-primary" />
                  Pickup order
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {field('name', 'Name *', { maxLength: 100 })}
                {field('phone', 'Phone number *', { type: 'tel', maxLength: 20 })}
                <ReadyTextOptIn checked={smsNotify} onChange={setSmsNotify} />
                {field('email', 'Email (for your receipt)', { type: 'email', maxLength: 255 })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Pickup time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(config?.pickup_options || ['asap']).map(option => (
                    <Button
                      key={option}
                      variant={pickupTime === option ? 'default' : 'outline'}
                      onClick={() => setPickupTime(option)}
                      data-testid={`pickup-${option}`}
                    >
                      {PICKUP_LABELS[option] || option}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <TipAndInstructions
              specialInstructions={specialInstructions}
              setSpecialInstructions={setSpecialInstructions}
              tip={tip}
              setTip={setTip}
              tipPercentage={tipPercentage}
              setTipPercentage={setTipPercentage}
              tipsEnabled={tipsEnabled}
              presets={config?.tip_presets?.length ? config.tip_presets : [15, 18, 20]}
            />
          </div>

          <div className="lg:col-span-1">
            <OrderSummaryCard
              items={items}
              subtotal={subtotalCents / 100}
              discount={discountCents / 100}
              appliedPromo={appliedPromo}
              taxRatePercent={rate}
              tax={taxCents == null ? null : taxCents / 100}
              tip={tipCents / 100}
              total={totalCents == null ? null : totalCents / 100}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              onApplyPromo={handleApplyPromo}
              onRemovePromo={() => setAppliedPromo(null)}
              promoChecking={promoChecking}
              onPay={handlePlaceOrder}
              isProcessing={placing}
              disabled={!config?.accepting_orders || !slug}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
